import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { GenericUtils } from '@unpispas/upp-defs';

import { Subject } from 'rxjs';

type EncryptedSessionPayload = {
    v: 1;
    iv: string;
    payload: string;
};

/**
 * @class storeService
 * @description Provides methods to interact with the storage system, including key-value operations (`Get`, `Set`, `Del`).
 * Supports scoped keys and ensures thread-safe access with a mutex-based locking mechanism.
 */
@Injectable({
    providedIn: 'root'
})
export class storeService {
    /**
     * The underlying storage instance.
     * Initialized using the `Initialize` method.
     */    
    private _storage: Storage | null = null;

    /**
     * Constructor to inject the storage dependency.
     * @param storage - An instance of `@ionic/storage` for data persistence.
     */    
    constructor(private storage: Storage){
        // nothing to do
    }

    /**
     * Initializes the storage instance and generates a unique tab/session identifier.
     * @returns A promise that resolves to `true` if the storage is initialized successfully, or `false` otherwise.
     */    
    private async Initialize(): Promise <boolean> {
        this._storage = await this.storage.create();
        return true;
    }

    /****************************/
    /* STORAGE ACCESS           */
    /****************************/

    /**
     * Retrieves a value from the storage by key.
     * @param key - The key associated with the stored value.
     * @returns A promise that resolves to the stored value (parsed as JSON if applicable) or `null` if the key does not exist.
     */    
    async Get(key: string): Promise <any | null> {
        if (!this._storage){
            await this.Initialize();
            if (!this._storage){
                return null;
            }
        }

        await this._AddCompleted(key);
        const _data = await this._storage.get(key);
        this._DelCompleted(key);

        if (_data){
            try {
                return JSON.parse(_data);
            }
            catch {
                return null;    // not json encoded
            }
        }

        return null;
    }

    /**
     * Stores a value in the storage under the specified key.
     * @param key - The key under which the value will be stored.
     * @param value - The value to be stored (serialized to JSON).
     * @returns A promise that resolves once the operation is complete.
     * @throws An error if the operation fails.
     */    
    async Set(key: string, value: any): Promise <void> {
        if (!this._storage){
            await this.Initialize();
            if (!this._storage){
                return;
            }
        }

        try {
            await this._AddCompleted(key);
            await this._storage.set(key, JSON.stringify(value))
            this._DelCompleted(key);
        }
        catch(error: any){
            throw new Error("[STORAGE] Cannot set value in key '" + key + "' (" + value.length + " bytes) - " + error.name + "(" + error.message + ")");
        }
    }

    /**
     * Deletes a value from the storage by key.
     * @param key - The key associated with the value to be deleted.
     * @returns A promise that resolves once the operation is complete.
     */    
    async Del(key: string): Promise <void> {
        console.info("[STORAGE] removing key '" + key + "'");
        if (!this._storage){
            return;     // not intialized
        }

        await this._AddCompleted(key);
        await this._storage.remove(key);
        this._DelCompleted(key);

        // check that it has been removed (and clear if not)
        const _data = await this.Get(key);
        if (_data){     // not removed workarround. Clear the content
            await this.Set(key, null);
        }
    }

    /****************************/
    /* SAVE ACCESS TO THE KEY   */
    /****************************/

    /**
     * A map of mutexes for managing concurrent access to keys.
     */    
    private _mutexes = new Map < string, Subject<void> > ();

    /**
     * Waits for the completion of any ongoing operation for the specified key.
     * @param key - The key to check for concurrent operations.
     * @returns A promise that resolves once the ongoing operation completes.
     */    
    private async _GetCompleted(key: string): Promise <void> {
        let _observable: any | null = null;

        const _subject = this._mutexes.get(key);
        if (_subject){
            _observable = _subject.asObservable();
        }

        if (_observable){     // writing in progress
            const _ini = performance.now();

            console.info("[STORE] waiting for write [" + key + "] to be completed..")
            await _observable;
            console.info("[STORE] write [" + key + "] completed in " + (performance.now() - _ini).toFixed(2) + " ms")
        }
    }

    /**
     * Adds a mutex lock for the specified key to prevent concurrent access.
     * @param key - The key to lock.
     * @returns A promise that resolves once the lock is added.
     */    
    private async _AddCompleted(key: string): Promise <void> {
        await this._GetCompleted(key);

        // the key is free: block next ones
        this._mutexes.set(key, new Subject<void>())
    }

    /**
     * Releases the mutex lock for the specified key.
     * @param key - The key to unlock.
     */    
    private _DelCompleted(key: string): void {
        const _subject = this._mutexes.get(key);
        if (_subject){
            _subject.next();        // notify
            _subject.complete();    // release
        }

        this._mutexes.delete(key);
    }

    /****************************/
    /* ENCRYPTED SESSION ACCESS */
    /****************************/

    private _sessionSealKey: Promise<CryptoKey> | null = null;
    private _secureReady: boolean | null = null;

    private readonly _sessionPrefix = 'upp:session:sealed:';
    private _SessionStorageKey(key: string): string {
        return this._sessionPrefix + key;
    }

    public SecureReady(): boolean {
        if (this._secureReady !== null){
            return this._secureReady;
        }

        let _ready = false;
        try {
            if ((typeof sessionStorage !== 'undefined') && !!globalThis.crypto?.subtle){
                const _probe = '__upp_secure_probe__';
                sessionStorage.setItem(_probe, '1');
                sessionStorage.removeItem(_probe);
                _ready = true;
            }
        }
        catch {
            _ready = false;
        }

        this._secureReady = _ready;
        return _ready;
    }

    async SecureGet(key: string): Promise<any | null> {
        if (!this.SecureReady()){
            return null;
        }

        const _sessionKey = this._SessionStorageKey(key);
        await this._AddCompleted(_sessionKey);
        const _raw = sessionStorage.getItem(_sessionKey);
        this._DelCompleted(_sessionKey);
        if (!_raw){
            return null;
        }

        let _sealed: EncryptedSessionPayload | null = null;
        try {
            _sealed = JSON.parse(_raw) as EncryptedSessionPayload;
        }
        catch {
            return null;
        }
        if (!_sealed || _sealed.v !== 1 || !_sealed.iv || !_sealed.payload){
            return null;
        }

        const _key = await this._SessionSealKey();
        if (!_key){
            return null;
        }

        try {
            const _iv = new Uint8Array(GenericUtils.base64ToArrayBuffer(_sealed.iv));
            const _payload = GenericUtils.base64ToArrayBuffer(_sealed.payload);
            const _decrypted = await globalThis.crypto.subtle.decrypt(
                {
                    name: 'AES-GCM',
                    iv: _iv
                },
                _key,
                _payload
            );
            const _json = new TextDecoder().decode(new Uint8Array(_decrypted));
            return JSON.parse(_json);
        }
        catch {
            return null;
        }
    }

    async SecureSet(key: string, value: any): Promise<void> {
        if (!this.SecureReady()){
            return;
        }

        const _sessionKey = this._SessionStorageKey(key);
        const _key = await this._SessionSealKey();
        if (!_key){
            return;
        }

        const _iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
        const _raw = JSON.stringify(value);
        const _bytes = new TextEncoder().encode(_raw);
        const _encrypted = await globalThis.crypto.subtle.encrypt(
            {
                name: 'AES-GCM',
                iv: _iv
            },
            _key,
            _bytes
        );
        const _sealed: EncryptedSessionPayload = {
            v: 1,
            iv: GenericUtils.arrayBufferToBase64(_iv.buffer),
            payload: GenericUtils.arrayBufferToBase64(_encrypted)
        };

        await this._AddCompleted(_sessionKey);
        sessionStorage.setItem(_sessionKey, JSON.stringify(_sealed));
        this._DelCompleted(_sessionKey);
    }

    async SecureDel(key: string): Promise<void> {
        if (!this.SecureReady()){
            return;
        }

        const _sessionKey = this._SessionStorageKey(key);
        await this._AddCompleted(_sessionKey);
        sessionStorage.removeItem(_sessionKey);
        this._DelCompleted(_sessionKey);
    }

    private async _SessionSealKey(): Promise<CryptoKey | null> {
        if (!this.SecureReady()){
            return null;
        }

        if (!this._sessionSealKey){
            this._sessionSealKey = globalThis.crypto.subtle.generateKey(
                {
                    name: 'AES-GCM',
                    length: 256
                },
                false,
                ['encrypt', 'decrypt']
            ) as Promise<CryptoKey>;
        }
        
        return await this._sessionSealKey;
    }
}
